// The Leave page (Session ASZ, D-V1, Director decision 28 September 2026: BUILD). A genuine minimum
// HR/leave record: who's away when, for the standby rota and dispatch to check against and warn on --
// never a block. No allowance tracking, no approval workflow, no accrual. Office/admin only; an
// engineer cannot self-serve leave in this build (small business, keep it simple).
//
// A SIMPLE LIST VIEW, not a calendar grid: ELE has a handful of engineers, and a sortable list of date
// ranges is legible at that scale (the same reasoning StandbyRotaPage gives for its own list-not-grid
// shape). RULE 1: Remove ARCHIVES (reversible), "Delete permanently" only on an archived record.
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { leaveApi, LEAVE_TYPES, LEAVE_TYPE_LABEL, type LeaveRecord, type LeaveType } from "../../lib/leave";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { ListToolbar } from "../../components/ListToolbar";
import { Loader } from "../../components/Loader";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { PageHeader } from "../../components/PageHeader";
import { SortableHeader } from "../../components/SortableHeader";
import { StatusPill } from "../../components/StatusPill";
import { formatDate } from "../../lib/format";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { filterRows, useListSearch, type SearchAccessors } from "../../lib/useListSearch";
import { sortRows, useSortableColumn, type SortAccessors } from "../../lib/useSortableColumn";

const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";
const labelCls = "mb-1 block font-menu text-xs tracking-wide text-ele-body";

// Search and sort run client-side over the rows the server returned for the engineer and Show archived filters.
const LEAVE_SEARCH: SearchAccessors<LeaveRecord> = [
  (r) => r.engineerName,
  (r) => LEAVE_TYPE_LABEL[r.type],
  (r) => r.notes,
  (r) => r.recordedByName,
  (r) => formatDate(r.startDate),
  (r) => formatDate(r.endDate),
];
const LEAVE_SORT: SortAccessors<LeaveRecord> = {
  engineer: (r) => r.engineerName,
  type: (r) => LEAVE_TYPE_LABEL[r.type],
  dates: (r) => r.startDate,
  recorded: (r) => r.recordedByName,
};
const SORT_OPTIONS = [
  { key: "engineer", label: "Engineer" },
  { key: "type", label: "Type" },
  { key: "dates", label: "Dates" },
];

function datesText(r: LeaveRecord): string {
  return r.startDate === r.endDate ? formatDate(r.startDate) : `${formatDate(r.startDate)} to ${formatDate(r.endDate)}`;
}

interface EngineerOption {
  id: string;
  name: string;
}

interface FormState {
  engineerId: string;
  startDate: string;
  endDate: string;
  type: LeaveType;
  notes: string;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

const EMPTY_FORM: FormState = { engineerId: "", startDate: todayIso(), endDate: todayIso(), type: "holiday", notes: "" };

function formFrom(r: LeaveRecord): FormState {
  return { engineerId: r.engineerId, startDate: r.startDate, endDate: r.endDate, type: r.type, notes: r.notes ?? "" };
}

export function LeavePage() {
  const [records, setRecords] = useState<LeaveRecord[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [engineerFilter, setEngineerFilter] = useState("");
  const [engineers, setEngineers] = useState<EngineerOption[]>([]);
  // Below md the list renders as cards (decided once at mount; jsdom has no matchMedia, so tests get the table).
  const [mobileCards] = useState(initialMobileLayout);
  const { search, setSearch } = useListSearch();
  const sort = useSortableColumn("leave");

  const [editing, setEditing] = useState<LeaveRecord | "new" | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [confirm, setConfirm] = useState<{ record: LeaveRecord; kind: "archive" | "delete" } | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const list = await leaveApi.list(engineerFilter ? { engineerId: engineerFilter } : {}, showArchived);
      setRecords(list);
      setLoadError(null);
    } catch (err) {
      setLoadError(friendlyApiError(err, "Could not load leave records."));
    }
  }, [engineerFilter, showArchived]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    api.scheduling
      .engineers()
      .then((rows) => setEngineers(rows.map((e) => ({ id: e.id, name: e.name })).sort((a, b) => a.name.localeCompare(b.name))))
      .catch(() => setEngineers([]));
  }, []);

  // The default order: newest start first, then engineer. A header click or the mobile sort select overrides it.
  const loaded = useMemo(
    () => [...(records ?? [])].sort((a, b) => (a.startDate === b.startDate ? a.engineerName.localeCompare(b.engineerName) : b.startDate.localeCompare(a.startDate))),
    [records],
  );
  const visible = useMemo(
    () => sortRows(filterRows(loaded, search, LEAVE_SEARCH), LEAVE_SORT, sort),
    [loaded, search, sort.sortKey, sort.sortDir],
  );

  function openNew() {
    setForm({ ...EMPTY_FORM, engineerId: engineerFilter || (engineers[0]?.id ?? "") });
    setFormError(null);
    setEditing("new");
  }

  function openEdit(r: LeaveRecord) {
    setForm(formFrom(r));
    setFormError(null);
    setEditing(r);
  }

  async function save() {
    if (!form.engineerId) {
      setFormError("Choose an engineer.");
      return;
    }
    if (!form.startDate || !form.endDate) {
      setFormError("Give both a start and an end date.");
      return;
    }
    if (form.endDate < form.startDate) {
      setFormError("The end date must be on or after the start date.");
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      if (editing === "new") {
        await leaveApi.create({ engineerId: form.engineerId, startDate: form.startDate, endDate: form.endDate, type: form.type, notes: form.notes.trim() || null });
      } else if (editing) {
        await leaveApi.update(editing.id, { startDate: form.startDate, endDate: form.endDate, type: form.type, notes: form.notes.trim() || null });
      }
      setEditing(null);
      await load();
    } catch (err) {
      setFormError(friendlyApiError(err, "Could not save this leave record. Please try again."));
    } finally {
      setSaving(false);
    }
  }

  async function runConfirm() {
    if (!confirm) return;
    setConfirmBusy(true);
    setConfirmError(null);
    try {
      if (confirm.kind === "archive") await leaveApi.archive(confirm.record.id);
      else await leaveApi.deletePermanently(confirm.record.id);
      setConfirm(null);
      await load();
    } catch (err) {
      setConfirmError(friendlyApiError(err, "That did not work. Please try again."));
    } finally {
      setConfirmBusy(false);
    }
  }

  async function restore(r: LeaveRecord) {
    setRowError(null);
    try {
      await leaveApi.restore(r.id);
      await load();
    } catch (err) {
      setRowError(friendlyApiError(err, "Could not restore this leave record. Please try again."));
    }
  }


  function clearAll() {
    setSearch("");
    setEngineerFilter("");
    setShowArchived(false);
  }

  function rowActions(r: LeaveRecord) {
    return r.archived ? (
      <>
        <Button variant="secondary" size="sm" onClick={() => void restore(r)}>
          Restore
        </Button>
        <Button
          variant="destructive"
          size="sm"
          onClick={() => {
            setConfirmError(null);
            setConfirm({ record: r, kind: "delete" });
          }}
        >
          Delete permanently
        </Button>
      </>
    ) : (
      <>
        <Button variant="ghost" size="sm" onClick={() => openEdit(r)} aria-label={`Edit ${r.engineerName}'s leave`}>
          Edit
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setConfirmError(null);
            setConfirm({ record: r, kind: "archive" });
          }}
          aria-label={`Remove ${r.engineerName}'s leave`}
        >
          Remove
        </Button>
      </>
    );
  }

  const typeTag = (r: LeaveRecord) => <StatusPill word={LEAVE_TYPE_LABEL[r.type]} tone="grey" />;
  const archivedTag = (r: LeaveRecord) => (r.archived ? <StatusPill word="Archived" tone="grey" variant="outline" /> : null);

  return (
    <div className="mx-auto max-w-4xl p-4 sm:p-6">
      <PageHeader
        className="mb-4"
        title="Leave"
        subtitle="Recorded holiday, sick leave and other time off. This is a record, not an approval workflow: the standby rota and dispatch check it and warn the office when someone is put on call or assigned into their own recorded leave, but nothing here ever blocks either."
        actions={
          <Button variant="secondary" size="sm" onClick={openNew}>
            Add leave
          </Button>
        }
      />

      <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-5" aria-label="Leave">
        <ListToolbar
          className="mb-3"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search engineer, type or notes..."
          searchLabel="Search leave"
          resultCount={{ shown: visible.length, total: loaded.length, noun: "leave records" }}
          sort={{ state: sort, options: SORT_OPTIONS, onChange: (st) => sort.setSort(st.sortKey, st.sortDir) }}
          onClear={clearAll}
          filters={
            <>
              <select value={engineerFilter} onChange={(e) => setEngineerFilter(e.target.value)} className={`${inputCls} w-auto`} aria-label="Filter by engineer">
                <option value="">Every engineer</option>
                {engineers.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name}
                  </option>
                ))}
              </select>
              <label className="flex items-center gap-2 font-menu text-xs tracking-wide text-ele-body">
                <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
                Show archived
              </label>
            </>
          }
        />

        {loadError && (
          <p role="alert" className="text-sm text-ele-danger">
            {loadError}
          </p>
        )}
        {rowError && (
          <p role="alert" className="mb-2 text-sm text-ele-danger">
            {rowError}
          </p>
        )}
        {!loadError && records === null && <Loader label="Loading the leave records..." />}
        {records !== null && loaded.length === 0 && <EmptyState message="No leave recorded yet." />}
        {records !== null && loaded.length > 0 && visible.length === 0 && (
          <EmptyState role="status" message={`No leave records match "${search.trim()}".`} />
        )}

        {visible.length > 0 &&
          (mobileCards ? (
            <MobileCardList label="Leave">
              {visible.map((r) => (
                <MobileListCard
                  key={r.id}
                  primary={r.engineerName}
                  secondary={`${datesText(r)} · recorded by ${r.recordedByName}`}
                  right={
                    <>
                      {typeTag(r)}
                      {archivedTag(r)}
                    </>
                  }
                  details={r.notes ? <p className="whitespace-pre-line text-xs text-ele-body">{r.notes}</p> : undefined}
                  actions={rowActions(r)}
                />
              ))}
            </MobileCardList>
          ) : (
            <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
              <table className="ele-list-table w-full text-left text-sm">
                <colgroup>
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "15%" }} />
                  <col style={{ width: "25%" }} />
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "20%" }} />
                </colgroup>
                <thead className="bg-ele-inset font-menu text-xs uppercase tracking-wide text-ele-blue">
                  <tr>
                    <SortableHeader label="Engineer" colKey="engineer" sort={sort} onToggle={sort.toggle} />
                    <SortableHeader label="Type" colKey="type" sort={sort} onToggle={sort.toggle} />
                    <SortableHeader label="Dates" colKey="dates" sort={sort} onToggle={sort.toggle} />
                    <SortableHeader label="Recorded by" colKey="recorded" sort={sort} onToggle={sort.toggle} />
                    <th className="px-3 py-2 cell-actions">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((r) => (
                    <tr key={r.id} className={`border-t border-ele-hairline align-top ${r.archived ? "opacity-60" : ""}`} data-testid="leave-row">
                      <td className="px-3 py-2 font-semibold text-ele-strong">{r.engineerName}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap items-center gap-1">
                          {typeTag(r)}
                          {archivedTag(r)}
                        </div>
                      </td>
                      <td className="cell-wrap px-3 py-2 text-ele-body">
                        <p>{datesText(r)}</p>
                        {r.notes && <p className="mt-1 whitespace-pre-line text-xs text-ele-body">{r.notes}</p>}
                      </td>
                      <td className="px-3 py-2 text-ele-body">{r.recordedByName}</td>
                      <td className="cell-actions px-3 py-2">
                        <div className="flex flex-wrap justify-end gap-2">{rowActions(r)}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

        {editing && (
          <ModalShell ariaLabel={editing === "new" ? "Add leave" : "Edit leave"} onClose={() => !saving && setEditing(null)} width="max-w-md">
            <h2 className="mb-4 font-heading text-xl text-ele-strong">{editing === "new" ? "Add leave" : "Edit leave"}</h2>
            <div className="space-y-3">
              <div>
                <label className={labelCls} htmlFor="leave-engineer">
                  Engineer
                </label>
                <select
                  id="leave-engineer"
                  value={form.engineerId}
                  onChange={(e) => setForm((f) => ({ ...f, engineerId: e.target.value }))}
                  className={inputCls}
                  disabled={editing !== "new"}
                >
                  <option value="">Choose an engineer</option>
                  {engineers.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls} htmlFor="leave-start">
                    Start date
                  </label>
                  <input id="leave-start" type="date" value={form.startDate} onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="leave-end">
                    End date
                  </label>
                  <input id="leave-end" type="date" value={form.endDate} onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))} className={inputCls} />
                </div>
              </div>
              <div>
                <label className={labelCls} htmlFor="leave-type">
                  Type
                </label>
                <select id="leave-type" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as LeaveType }))} className={inputCls}>
                  {LEAVE_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {LEAVE_TYPE_LABEL[t]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls} htmlFor="leave-notes">
                  Notes
                </label>
                <textarea id="leave-notes" rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className={inputCls} />
              </div>
            </div>
            {formError && (
              <p role="alert" className="mt-3 rounded-ele border border-ele-danger px-3 py-2 text-sm text-ele-strong">
                {formError}
              </p>
            )}
            <div className={`mt-5 gap-2 ${MODAL_ACTIONS}`}>
              <Button variant="ghost" className={MODAL_ACTION_BTN} onClick={() => setEditing(null)} disabled={saving}>
                Cancel
              </Button>
              <Button className={MODAL_ACTION_BTN} onClick={() => void save()} disabled={saving}>
                {saving ? "Saving..." : editing === "new" ? "Add leave" : "Save changes"}
              </Button>
            </div>
          </ModalShell>
        )}

        {confirm && (
          <ConfirmDeleteModal
            open
            variant={confirm.kind === "archive" ? "archive" : "destructive"}
            title={confirm.kind === "archive" ? `Remove ${confirm.record.engineerName}'s leave?` : `Delete this leave record permanently?`}
            message={
              confirm.kind === "archive"
                ? "This archives the record: it leaves this list, stops warning the standby rota and dispatch, and can be restored from Show archived."
                : "This deletes the leave record for good. It cannot be undone."
            }
            confirmLabel={confirm.kind === "archive" ? "Remove" : "Delete permanently"}
            busy={confirmBusy}
            error={confirmError}
            onConfirm={() => void runConfirm()}
            onCancel={() => !confirmBusy && setConfirm(null)}
          />
        )}
      </section>
    </div>
  );
}
